import api from '../../../shared/utils/api';

export type LinkedInActionType = 'conexao' | 'mensagem';

export interface LinkedInOutreachRequest {
  contactLinkedinUrl: string;
  dealId?: string;
  prospectId?: string;
  actionType: LinkedInActionType;
  contactName?: string;
}

export interface LinkedInOutreachResponse {
  deepLinkUrl: string;
  activityId: string;
}

export interface AutoConnectRequest {
  radar_generation_id?: string;
  profile_url: string;
  note?: string;
  deal_id?: string;
  prospect_id?: string;
  contact_name?: string;
  email?: string;
  deal_name?: string;
  // Fire-and-forget: com o contexto do passo, o SERVIDOR conclui o passo da
  // cadência no desfecho do job (writeback do crm-integration).
  cadence_id?: string;
  step_id?: string;
}

export interface AutoMessageRequest {
  radar_generation_id?: string;
  profile_url: string;
  message: string;
  deal_id?: string;
  prospect_id?: string;
  contact_name?: string;
  deal_name?: string;
  cadence_id?: string;
  step_id?: string;
}

export interface OutreachJobResponse {
  job_id: string;
  status: string;
}

export type ConnectionWatchStatusType =
  | 'not_requested'
  | 'pending_verification'
  | 'accepted'
  | 'expired_no_response'
  | 'withdrawn'
  | 'rate_limited'
  | 'error'
  | string;

export interface ConnectionWatchItem {
  profile_url: string;
  status: ConnectionWatchStatusType;
  can_invite: boolean;
  disabled_reason?: 'already_pending' | 'already_connected' | null;
  last_check_at?: string | null;
  next_check_at?: string | null;
  accepted_at?: string | null;
  attempts?: number;
  backoff_stage?: number;
  error_message?: string | null;
  cooldown_expires_at?: string | null;
  withdrawn_at?: string | null;
  withdrawn_reason?: string | null;
}

export interface ConnectionStatusBatchResponse {
  watches: Record<string, ConnectionWatchItem>;
}

const linkedinOutreachApi = {
  /**
   * Manual mode: logs the outreach attempt and returns a deep link
   * for the user to open LinkedIn in a new tab and act manually.
   *
   * BUG real medido em produção (2026-08-30): chamava `/sales/linkedin-outreach`
   * (404) — o BFF monta o `salesRouter` de CRUD (`routes/sales/index.ts`) FLAT
   * em `/api/v1` (sem segmento `/sales`; ver `router.use('/linkedin-outreach',
   * linkedinOutreachRouter)` lá), então o caminho de verdade é
   * `/api/v1/linkedin-outreach`. O prefixo `/sales/` só existe para o router de
   * IA em `routes/intelligence/sales.routes.ts` (bdr-hook, cold-email,
   * approach-hook, generate-whatsapp) — namespace DIFERENTE, mesma palavra.
   */
  logAndOpenLinkedIn: async (
    payload: LinkedInOutreachRequest,
  ): Promise<LinkedInOutreachResponse> => {
    const { data } = await api.post<LinkedInOutreachResponse>(
      '/linkedin-outreach',
      payload,
    );
    return data;
  },

  /**
   * Automated mode: enqueues a connection request via Playwright.
   * Requires an active LinkedIn session (configured via the bookmarklet).
   */
  autoConnect: async (payload: AutoConnectRequest): Promise<OutreachJobResponse> => {
    const { data } = await api.post<OutreachJobResponse>(
      '/crm/outreach/connect',
      payload,
    );
    return data;
  },

  /**
   * Automated mode: enqueues a LinkedIn direct message via Playwright.
   */
  autoMessage: async (payload: AutoMessageRequest): Promise<OutreachJobResponse> => {
    const { data } = await api.post<OutreachJobResponse>(
      '/crm/outreach/message',
      payload,
    );
    return data;
  },

  /**
   * Poll the status of an automated outreach job.
   */
  getJobStatus: async (jobId: string): Promise<{ status: string; result?: unknown }> => {
    const { data } = await api.get<{ status: string; result?: unknown }>(
      `/crm/outreach/jobs/${jobId}`,
    );
    return data;
  },

  /** Cancela um envio em voo. `cancelled`=cancelado em definitivo;
   *  `cancel_requested`=pedido registrado num job em execução (best-effort). */
  cancelJob: async (
    jobId: string,
  ): Promise<{ status: string; cancelled: boolean; cancel_requested?: boolean }> => {
    const { data } = await api.delete<{
      status: string;
      cancelled: boolean;
      cancel_requested?: boolean;
    }>(`/crm/outreach/jobs/${jobId}`);
    return data;
  },

  /** Uso diário do LinkedIn do usuário (contador de cota no composer). */
  getQuota: async (): Promise<LinkedInQuota> => {
    const { data } = await api.get<LinkedInQuota>('/crm/outreach/quota');
    return data;
  },

  /** Consulta status das solicitações de conexão em lote por URLs de perfil */
  getConnectionStatusBatch: async (
    profileUrls: string[],
  ): Promise<ConnectionStatusBatchResponse> => {
    if (!profileUrls || profileUrls.length === 0) {
      return { watches: {} };
    }
    const { data } = await api.post<ConnectionStatusBatchResponse>(
      '/crm/outreach/connection-watches/status-batch',
      { profile_urls: profileUrls },
    );
    return data;
  },

  /** Diagnóstico preventivo de segurança e reputação da conta LinkedIn do vendedor (RN-03) */
  getAccountHealth: async (): Promise<LinkedInAccountHealth> => {
    const { data } = await api.get<LinkedInAccountHealth>('/crm/outreach/account-health');
    return data;
  },
};

export interface LinkedInAccountHealth {
  user_id: number;
  score: number;
  pending_invites_count: number;
  weekly_invites_sent: number;
  acceptance_rate: number;
  risk_level: 'safe' | 'moderate' | 'critical' | string;
  calculated_at: string;
}

export interface LinkedInQuotaBucket {
  used: number;
  limit: number;
  remaining: number;
}
export interface LinkedInQuota {
  connect: LinkedInQuotaBucket;
  message: LinkedInQuotaBucket;
}

export default linkedinOutreachApi;

